<template>
  <div class="relative flex-1 cursor-text">
    <label
      v-if="id"
      class="mb-2 min-w-max cursor-text text-sm font-semibold"
      :for="id"
      >{{ label }}</label
    >
    <input
      :id="id"
      ref="input"
      v-model="content"
      v-maska
      :data-maska="dataMaska"
      v-bind="{ required, readonly, type, placeholder, autocomplete }"
      :maxlength="maxLength"
      :required="required"
      :readonly="readonly"
      class="peer mt-1.5 w-full transform-gpu bg-gray-200 px-4 py-[11px] text-base ring-0 transition-colors duration-200 invalid:border-red-500 out-of-range:border-red-500 focus:ring-1 focus:ring-black"
      :class="{
        'placeholder:text-transparent': !placeholder,
        'default:border-secondary-700 hover:border-black focus:border-secondary-700 focus:outline-none':
          appearance === 'default',
        'border-red-500 default:border-red-500 hover:border-red-700':
          appearance === 'danger',
        '': !disabled,
        ' cursor-not-allowed border-gray-200 text-gray-600': disabled,
      }"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
    />
  </div>
</template>

<script setup lang="ts">
import { vMaska } from 'maska'
import { dash } from 'radash'

const props = defineProps({
  value: {
    type: [String] as PropType<string>,
    default: '',
  },
  type: {
    type: String as PropType<string>,
    default: 'text',
  },
  appearance: {
    type: String as PropType<'default' | 'danger'>,
    default: 'default',
    validator(value: string) {
      return ['default', 'danger'].includes(value)
    },
  },
  autocomplete: {
    type: String,
    default: '',
  },
  label: {
    type: String as PropType<string>,
    required: false,
    default: undefined,
  },
  mask: {
    type: String as PropType<string>,
    default: undefined,
  },
  placeholder: {
    type: String as PropType<string>,
    default: undefined,
  },
  required: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  pattern: {
    type: String as PropType<string>,
    default: undefined,
  },
  name: {
    type: String as PropType<string>,
    default: undefined,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: undefined,
  },
  hint: {
    type: String,
    default: '',
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
})

const emit = defineEmits(['update:value'])

const content = computed({
  get: () => props.value,
  set: (newValue: string): void => emit('update:value', newValue),
})

const dataMaska = computed(() => {
  // Maska don't accept array for the dynamic mask approach.
  // String conversion is needed in order to make it work properly
  return Array.isArray(props.mask) ? JSON.stringify(props.mask) : props.mask
})

const id = computed(() => props.label && dash(props.label))
</script>
